import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  EFFORT_OPTS as EFFORT_VALUES,
  MODEL_OPTS as MODEL_VALUES,
} from "@tinsmith/model-options";

import { DevboxImage } from "./DevboxImage";
import { SaveBar, SettingsRow } from "./parts";
import { RepoIntegrations } from "./RepoIntegrations";
import { RepoSecrets } from "./RepoSecrets";
import styles from "./Settings.module.css";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { RepoAvatar } from "../../components/RepoAvatar";
import { Select } from "../../components/Select";
import {
  patchRepoSettings,
  removeRepository,
  repoLabel,
  type RepositoryCard,
  useRepoBranches,
  useRepoSettings,
  useRepository,
} from "../../data/api/repositories";
import type { ApiRepoSettingsRead } from "../../data/api/types";
import { Icon } from "../../icons";
import { useNav } from "../../routing/useNav";

const MODEL_OPTS = [
  { label: "inherit workspace default", value: "inherit" },
  ...MODEL_VALUES.map((opt) => ({ label: opt.value, value: opt.value })),
];

const EFFORT_OPTS = [
  { label: "inherit workspace default", value: "inherit" },
  ...EFFORT_VALUES.map((opt) => ({ label: opt.value, value: opt.value })),
];

type FormState = {
  readonly base: string;
  readonly effort: string;
  readonly model: string;
};

const toForm = (
  settings: ApiRepoSettingsRead | undefined,
  defaultBranch: string,
): FormState => ({
  base: settings?.default_base_branch ?? defaultBranch,
  effort: settings?.default_effort ?? "inherit",
  model: settings?.default_model ?? "inherit",
});

export const RepoSettings = () => {
  const { repoId = "" } = useParams();
  const {
    error: repoError,
    loading: repoLoading,
    repository,
  } = useRepository(repoId || null);
  const {
    loading: settingsLoading,
    refetch: refetchSettings,
    settings,
  } = useRepoSettings(repoId || null);
  useDocumentTitle("Settings", repository ? repoLabel(repository) : repoId);

  if ((repoLoading || settingsLoading) && !repository) {
    return (
      <div className={styles.repoLoading}>Loading repository settings…</div>
    );
  }
  if (repoError || !repository) {
    return (
      <EmptyState
        body="This repository isn't connected, or the link is stale."
        title="Repository not found."
      />
    );
  }

  // Mount the form only once repo (and thus its defaults) are known, so the
  // form state initialises from live data without a stale-state effect.
  return (
    <RepoSettingsForm
      devboxCacheClearedAt={settings?.devbox_cache_cleared_at ?? null}
      initial={toForm(settings, repository.branch)}
      onDevboxCacheCleared={refetchSettings}
      repo={repository}
      repoId={repoId}
    />
  );
};

type RepoSettingsFormProps = {
  /** When the devbox image cache was cleared, if no build has honoured it yet. */
  readonly devboxCacheClearedAt: string | null;
  readonly initial: FormState;
  readonly onDevboxCacheCleared: () => void;
  readonly repo: RepositoryCard;
  readonly repoId: string;
};

const RepoSettingsForm = ({
  devboxCacheClearedAt,
  initial,
  onDevboxCacheCleared,
  repo,
  repoId,
}: RepoSettingsFormProps) => {
  const nav = useNav();
  const [saved, setSaved] = useState<FormState>(initial);
  const [form, setForm] = useState<FormState>(initial);
  const [flash, setFlash] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(saved) !== JSON.stringify(form);

  const save = (): void => {
    if (busy) {
      return;
    }
    setBusy(true);
    void patchRepoSettings(repoId, {
      default_base_branch: form.base,
      default_effort: form.effort === "inherit" ? null : form.effort,
      default_model: form.model === "inherit" ? null : form.model,
    })
      .then(() => setSaved(form))
      .catch(() => undefined)
      .finally(() => {
        setBusy(false);
        setFlash(true);
        setTimeout(() => setFlash(false), 1800);
      });
  };

  const disconnect = (): void => {
    void removeRepository(repoId)
      .catch(() => undefined)
      .finally(() => nav.dashboard());
  };

  // The repo's real branches (GET /branches), default branch first. Always keep
  // the repo default and the currently-saved base present so the Select has a
  // valid option before /branches loads or if the saved branch is stale.
  const { branches } = useRepoBranches(repoId);
  const baseOpts = [
    ...new Set([
      repo.branch || "main",
      ...branches.map((b) => b.name),
      form.base,
    ]),
  ]
    .filter(Boolean)
    .map((b) => ({ label: b, value: b }));

  // Non-terminal agents that disconnecting will cancel (the server enqueues a
  // cancel for each). Drawn from the repo card's live counts so the warning is
  // honest about the blast radius.
  const activeAgents =
    repo.agents.needsInput +
    repo.agents.pending +
    repo.agents.review +
    repo.agents.running;
  const disconnectHelp =
    activeAgents > 0
      ? `Cancels ${activeAgents} active agent${activeAgents === 1 ? "" : "s"} and removes Tinsmith's access. History is kept.`
      : "Removes Tinsmith's access. History is kept.";

  return (
    <div className={styles.settings}>
      <div className={styles.header}>
        <div className={styles.repoTitle}>
          <RepoAvatar org={repo.org} size={38} />
          <div>
            <h1 className={styles.repoTitleText}>
              {repo.org}/{repo.name}
            </h1>
            <div className={styles.repoSub}>
              <Icon.Branch
                size={11}
                style={{ marginRight: 3, verticalAlign: -1 }}
              />
              {repo.branch} · repository settings
            </div>
          </div>
        </div>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Defaults</h3>
        <div className={styles.card}>
          <SettingsRow help="New agents branch from here." label="Base branch">
            <Select
              onValueChange={(v) => set("base", v)}
              options={baseOpts}
              value={form.base}
            />
          </SettingsRow>
          <SettingsRow
            help="Inherit the workspace default, or pin one for this repo."
            label="Model override"
          >
            <Select
              onValueChange={(v) => set("model", v)}
              options={MODEL_OPTS}
              value={form.model}
            />
          </SettingsRow>
          <SettingsRow
            help="Inherit the workspace default, or pin one for this repo."
            label="Effort override"
          >
            <Select
              onValueChange={(v) => set("effort", v)}
              options={EFFORT_OPTS}
              value={form.effort}
            />
          </SettingsRow>
        </div>
      </section>

      <RepoSecrets repoId={repoId} />
      <RepoIntegrations repoId={repoId} />

      <DevboxImage
        clearedAt={devboxCacheClearedAt}
        onCleared={onDevboxCacheCleared}
        repoId={repoId}
      />

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Danger zone</h3>
        <div className={`${styles.card} ${styles.danger}`}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <div className={styles.label}>Disconnect repository</div>
              <div className={styles.help}>{disconnectHelp}</div>
            </div>
            <Button onClick={disconnect} size="sm" variant="danger">
              Disconnect
            </Button>
          </div>
        </div>
      </section>

      <SaveBar
        dirty={dirty}
        flash={flash}
        onDiscard={() => setForm(saved)}
        onSave={save}
      />
    </div>
  );
};
